/**
 * 동기화 대기열 항목 카드
 * Signal & Noise: 실패는 강렬하게, 성공은 절제되게
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MACHINE_TITLES, type MachineKey } from "@ict/shared";
import type { SyncQueueItem } from "@/lib/db/types";
import { getCachedProject } from "@/lib/db/projects";
import { getCachedMachinesByProject } from "@/lib/db/machines";
import { getLocalPhoto } from "@/lib/db/photos";
import { getActionLabel, summarizePayload } from "@/lib/sync-labels";

/** 타임스탬프 → "2/15 14:30:05" 형식 */
function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  const M = d.getMonth() + 1;
  const D = d.getDate();
  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  const s = d.getSeconds().toString().padStart(2, "0");
  return `${M}/${D} ${h}:${m}:${s}`;
}

interface SyncQueueItemCardProps {
  item: SyncQueueItem;
  showActions?: boolean;
  onRetry?: () => void;
  onDelete?: () => void;
  /** 외부에서 해석된 프로젝트명 (있으면 내부 조회 건너뜀) */
  resolvedProjectName?: string;
  /** 외부에서 해석된 설비명 (있으면 내부 조회 건너뜀) */
  resolvedMachineName?: string;
}

/**
 * 동기화 대기열 항목 카드 컴포넌트
 */
export function SyncQueueItemCard({
  item,
  showActions = false,
  onRetry,
  onDelete,
  resolvedProjectName,
  resolvedMachineName,
}: SyncQueueItemCardProps) {
  const [localProjectName, setLocalProjectName] = useState<string>("");
  const [localMachineName, setLocalMachineName] = useState<string>("");
  const [photoThumbnail, setPhotoThumbnail] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // 외부에서 이름이 제공되지 않은 경우에만 내부 조회 (pending 탭 호환)
  useEffect(() => {
    if (resolvedProjectName !== undefined && resolvedMachineName !== undefined) return;

    async function loadNames() {
      if (resolvedProjectName === undefined && item.projectId) {
        const project = await getCachedProject(item.projectId);
        if (project) {
          setLocalProjectName(project.name);
        }
      }

      if (resolvedMachineName === undefined && item.projectId && item.machineId) {
        const machines = await getCachedMachinesByProject(item.projectId);
        const machine = machines.find(
          (m) => m.machine._id === item.machineId || m.machine.machineId === item.machineId
        );
        if (machine) {
          setLocalMachineName(machine.machine.name);
        }
      }
    }

    loadNames();
  }, [item.projectId, item.machineId, resolvedProjectName, resolvedMachineName]);

  const projectName = resolvedProjectName ?? localProjectName;
  const machineName = resolvedMachineName ?? localMachineName;

  // 사진 썸네일 로드
  useEffect(() => {
    if (item.type !== "photo") return;
    const photoId = (item.payload.photoId as string) || item.entityId;
    if (!photoId) return;

    getLocalPhoto(photoId).then((photo) => {
      if (photo?.base64Thumbnail) {
        setPhotoThumbnail(photo.base64Thumbnail);
      }
    });
  }, [item.type, item.payload.photoId, item.entityId]);

  // 1줄: 타입 + 작업 유형 (예: "점검항목 수정")
  const actionLabel = getActionLabel(item.type, item.operation, item.payload);

  // 2줄: 경로 (프로젝트명 > 설비명 #번호)
  const pathParts: string[] = [];
  if (projectName) {
    pathParts.push(projectName);
  }
  if (machineName) {
    // docKey에서 번호 추출 (예: "doc-1" → "#1")
    let machineLabel = machineName;
    if (item.docKey) {
      const match = item.docKey.match(/^doc-(\d+)$/);
      if (match) {
        machineLabel = `${machineName} #${match[1]}`;
      }
    }
    pathParts.push(machineLabel);
  }
  const pathLabel = pathParts.join(" > ");

  // 3줄: 변경 내용 요약 (photo 타입은 표시하지 않음)
  let changesSummary = item.type === "photo" ? "" : summarizePayload(item.payload, item.type);

  // 설비 추가 시 설비명 + 개수 표시
  if (item.type === "equipment" && item.operation === "create" && item.createdMachineIds) {
    const machineKey = (item.payload.machineKey as string) || item.docKey || "";
    const machineTitleLabel = MACHINE_TITLES[machineKey as MachineKey] || machineKey;
    changesSummary = `${machineTitleLabel} ${item.createdMachineIds.length}개 추가`;
  }

  const isFailed = item.status === "failed";

  return (
    <div
      className={`rounded-xl border transition-all overflow-hidden ${
        isFailed ? "border-red-200 border-l-4 border-l-red-500" : "border-slate-200"
      }`}
    >
      {/* 카드 콘텐츠 */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* 1줄: 타입 + 작업 */}
            <p className="font-semibold text-slate-900">{actionLabel}</p>

            {/* 2줄: 경로 */}
            {pathLabel && <p className="text-sm truncate mt-1 text-slate-600">{pathLabel}</p>}

            {/* 3줄: 변경 내용 (여러 줄 지원) */}
            {changesSummary && (
              <p className="text-xs mt-1.5 whitespace-pre-line font-mono text-slate-500">
                {changesSummary}
              </p>
            )}
          </div>

          {/* 우측 썸네일 (세로 중앙, 클릭 시 미리보기) */}
          {photoThumbnail && (
            <button className="flex-shrink-0" onClick={() => setShowPreview(true)}>
              <img
                src={photoThumbnail}
                alt=""
                className="w-16 h-16 rounded-xl object-cover ring-1 ring-slate-200"
              />
            </button>
          )}
        </div>

        {/* 완료 시간 정보 */}
        {item.status === "completed" && (
          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
            <span>작업 {formatTimestamp(item.createdAt)}</span>
            <span className="text-slate-300">→</span>
            {item.syncedAt && <span>동기화 {formatTimestamp(item.syncedAt)}</span>}
          </div>
        )}

        {/* 에러 메시지 */}
        {item.lastError && (
          <div className="mt-3 p-3 rounded-lg border border-red-200 bg-red-50/30">
            <p className="text-xs text-slate-700 font-mono">{item.lastError}</p>
          </div>
        )}
      </div>

      {/* 하단 액션 바 (미완료 항목에 표시) */}
      {showActions && item.status !== "completed" && (
        <div className="border-t border-slate-200 bg-slate-50/50 px-4 py-2.5 flex gap-2">
          {isFailed && (
            <button
              onClick={() => onRetry?.()}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold active:bg-blue-700 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              재시도
            </button>
          )}
          <button
            onClick={() => onDelete?.()}
            className={`flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-semibold active:bg-slate-50 transition-colors ${
              !isFailed ? "flex-1" : ""
            }`}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            삭제
          </button>
        </div>
      )}
      {/* 사진 미리보기 모달 */}
      {showPreview &&
        photoThumbnail &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/90"
            onClick={() => setShowPreview(false)}
          >
            <img
              src={photoThumbnail}
              alt=""
              className="max-h-[80vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-0 left-0 right-0 pb-safe">
              <div className="flex justify-center pb-8">
                <button
                  onClick={() => setShowPreview(false)}
                  className="flex items-center justify-center gap-2 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 px-8 py-3.5 text-sm font-semibold text-white active:scale-[0.98] active:bg-white/20 transition-all duration-150"
                >
                  <span className="text-lg">&times;</span>
                  닫기
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default SyncQueueItemCard;
